import React, {useEffect, useState} from "react";
import {Button, Form, Input, Modal, notification, Select, Spin, Table, Tooltip,} from "antd";
import {DeleteOutlined, EditOutlined} from "@ant-design/icons";
import SharedService from "../../../services/shared/service";
import {useAuth} from "oidc-react";
import {useJwt} from "react-jwt";
import SEO from "../../../components/SEO"

const Admin = () => {
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [emailAddress, setEmailAddress] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [listPagination, setlistPagination] = useState({
        totalItemCount: 0,
        pageSize: 10,
        pageNumber: 1,
    });

    const pageSEOSettings = {
        title: "Email Whitelist",
    };

    const [sortedInfo, setSortedInfo] = useState({});
    const [form] = Form.useForm();
    const [selectedRecord, setSelectedRecord] = useState(null);
    const {userData, userManager} = useAuth();
    const token = userData?.access_token;
    const {decodedToken} = useJwt(token);
    const userEmail = decodedToken?.email;

    useEffect(() => {
        fetchWhiteList();
    }, []);

    const refreshAccessToken = async () => {
        try {
            await userManager.signinSilent();
        } catch (error) {
            console.error("Token refresh failed:", error);
        }
    };

    useEffect(() => {
        const refreshInterval = setInterval(() => {
            refreshAccessToken();
        }, 600000);

        return () => clearInterval(refreshInterval);
    }, [userManager]);

    useEffect(() => {
        if (listPagination.totalItemCount !== 0) {
            fetchWhiteList();
        }
    }, [listPagination.pageNumber, sortedInfo]);

    const showAddModal = () => {
        setAddModalVisible(true);
    };

    const showEditModal = (record) => {
        form.setFieldsValue({
            id: record.id,
            email: record.value,
            type: record.type,
        });
        setEditModalVisible(true);
    };

    const showDeleteModal = (record) => {
        setSelectedRecord(record);
        setDeleteModalVisible(true);
    };

    const handleCancel = () => {
        setAddModalVisible(false);
        setEditModalVisible(false);
        setDeleteModalVisible(false);
    };

    const handleAdd = () => {
        form.validateFields().then((values) => {
            addWhiteList(values.addemail, values.addtype);
            form.resetFields();
            setAddModalVisible(false);
        });
    };

    const handleEdit = () => {
        form.validateFields().then((values) => {
            form.resetFields();
            editWhiteList(values.email, values.type, parseInt(values.id, 10));
            setEditModalVisible(false);
        });
    };

    const handleDelete = () => {
        deleteWhitelist(selectedRecord);
        setDeleteModalVisible(false);
    };

    const addWhiteList = async (email, type) => {
        setLoading(true);
        try {
            const data = {
                value: email,
                type: type,
                updatedBy: userEmail,
            };

            const response = await SharedService.post({
                token,
                url: "emailwhitelist",
                data,
            });
            if (response) {
                notification.success({
                    message: "Added Successfully",
                });
                fetchWhiteList();
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            if (error.response && error.response.status === 500) {
                notification.error({
                    message: "Error",
                    description: error.response.statusText,
                });
            }
            if (error.response && error.response.status === 502) {
                notification.error({
                    message: "Error",
                    description: error.response.statusText,
                });
            }
        }
    };

    const editWhiteList = (email, type, id) => {
        setLoading(true);
        const data = {
            Id: id,
            value: email,
            type: type,
            updatedBy: userEmail,
        };

        return SharedService.put({token, url: "emailwhitelist", data})
            .then(() => {
                notification.success({
                    message: "Updated Successfully",
                });
                fetchWhiteList();
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                if (error.response && error.response.status === 500) {
                    notification.error({
                        message: "Error",
                        description: error.response.statusText,
                    });
                }
                if (error.response && error.response.status === 502) {
                    notification.error({
                        message: "Error",
                        description: error.response.statusText,
                    });
                }
                return false;
            });
    };

    const fetchWhiteList = async () => {
        setLoading(true);
        try {
            const params = {
                pageNumber: listPagination.pageNumber,
                pageSize: listPagination.pageSize,
                orderBy:
                    sortedInfo.columnKey !== undefined && sortedInfo.order !== undefined
                        ? `${sortedInfo.columnKey}:${sortedInfo.order === "ascend" ? "Asc" : "Desc"}`
                        : "id:Asc",
            };

            const response = await SharedService.get({
                token,
                url: "emailwhitelist",
                params,
            });
            setEmailAddress(response.items);
            setlistPagination({
                ...listPagination,
                totalItemCount: response.totalItemCount,
            });
            setLoading(false);
        } catch (error) {
            setLoading(false);
            if (error.response && error.response.status === 500) {
                notification.error({
                    message: "Error",
                    description: error.response.statusText,
                });
            }
            if (error.response && error.response.status === 502) {
                notification.error({
                    message: "Error",
                    description: error.response.statusText,
                });
            }
        }
    };

    const handleTableChange = (pagination, filters, sorter) => {
        setSortedInfo(sorter);
        setlistPagination({...listPagination, pageNumber: pagination.current});
    };

    const paginationProps = {
        showSizeChanger: false,
        total: listPagination.totalItemCount,
        pageSize: listPagination.pageSize,
        current: listPagination.pageNumber,
    };

    const deleteWhitelist = (id) => {
        setLoading(true);
        SharedService.delete({token, url: `emailwhitelist/${id}`})
            .then(() => {
                setLoading(false);
                notification.success({
                    message: "Deleted Successfully",
                });
                fetchWhiteList();
            })
            .catch((error) => {
                setLoading(false);
                if (error.response && error.response.status === 500) {
                    notification.error({
                        message: "Error",
                        description: error.response.statusText,
                    });
                }
                if (error.response && error.response.status === 502) {
                    notification.error({
                        message: "Error",
                        description: error.response.statusText,
                    });
                }
            });
    };

    const columns = [
        {
            title: "Id",
            key: "id",
            dataIndex: "id",
            render: (id) => (
                <Tooltip title={id} placement="topLeft">
                    <div>{id}</div>
                </Tooltip>
            ),
            width: 100,
            align: "left",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "id" && sortedInfo.order,
        },
        {
            title: "Email Address",
            key: "value",
            dataIndex: "value",
            render: (value) => (
                <Tooltip title={value} placement="topLeft">
                    <div>{value}</div>
                </Tooltip>
            ),
            align: "left",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "value" && sortedInfo.order,
        },
        {
            title: "Type",
            key: "type",
            dataIndex: "type",
            render: (value) => {
                const label = value === 0 ? "Domain" : "Address";
                return (
                    <Tooltip title={label} placement="topLeft">
                        <div>{label}</div>
                    </Tooltip>
                );
            },
            align: "left",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "type" && sortedInfo.order,
        },
        {
            title: (
                <div className="grid grid-cols-12">
                    <div className="col-span-8"></div>
                    <div className="col-span-4 ml-4">Actions</div>
                </div>
            ),
            key: "actions",
            render: (text, record) => (
                <div className="flex justify-end gap-3">
                    <Tooltip title="Edit">
                        <EditOutlined
                            style={{fontSize: 20}}
                            onClick={() => showEditModal(record)}
                            className="cursor-pointer"
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <DeleteOutlined
                            style={{fontSize: 20}}
                            onClick={() => showDeleteModal(record.id)}
                            className="cursor-pointer text-red-500"
                        />
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <div>
            <SEO {...pageSEOSettings} />
            <div className="px-4 sm:px-7 py-5">
                <div className="flex items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="text-[22px] font-semibold text-[#0B1220] leading-tight">Permitted emails</div>
                        <div className="text-[13px] text-[#7A849A] mt-1">
                            Whitelist of approved sender addresses. Only mail from these addresses enters the queues.
                        </div>
                    </div>
                    <Button type="primary" onClick={showAddModal}>Add email</Button>
                </div>
            </div>

            <div className="px-4 sm:px-7 pb-7 relative">
                {loading && (
                    <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-10 rounded-[14px]">
                        <Spin size="large" className="custom-spin"/>
                    </div>
                )}
                <Table
                    dataSource={emailAddress}
                    rowKey="id"
                    columns={columns}
                    pagination={paginationProps}
                    onChange={handleTableChange}
                />
            </div>

            {/* Add Modal */}
            <Modal
                title="Add email"
                open={addModalVisible}
                onOk={handleAdd}
                onCancel={handleCancel}
                okText="Add"
                cancelText="Cancel"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="addemail"
                        label={<span className="text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide">Email</span>}
                    >
                        <Input placeholder="Enter the Email"/>
                    </Form.Item>
                    <Form.Item
                        name="addtype"
                        label={<span className="text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide">Type</span>}
                    >
                        <Select placeholder="Select the type">
                            <Select.Option value={0}>Domain</Select.Option>
                            <Select.Option value={1}>Address</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="Edit email"
                open={editModalVisible}
                onOk={handleEdit}
                onCancel={handleCancel}
                okText="Save"
                cancelText="Cancel"
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="id" label={<span className="text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide">ID</span>}>
                        <Input readOnly disabled={true}/>
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label={<span className="text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide">Email</span>}
                    >
                        <Input placeholder="Enter the Email"/>
                    </Form.Item>
                    <Form.Item
                        name="type"
                        label={<span className="text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide">Type</span>}
                    >
                        <Select placeholder="Select the Type">
                            <Select.Option value={0}>Domain</Select.Option>
                            <Select.Option value={1}>Address</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Delete Modal */}
            <Modal
                title="Delete email"
                open={deleteModalVisible}
                onOk={handleDelete}
                onCancel={handleCancel}
                okText="Delete"
                okButtonProps={{danger: true}}
                cancelText="Cancel"
                centered
            >
                <div className="text-[14px] text-[#374152]">
                    Are you sure you want to delete this email?
                </div>
            </Modal>
        </div>
    );
};

export default Admin;
